import { useState, useRef, useMemo, useEffect } from "react";
import GeneratingCountdown from "@/components/GeneratingCountdown";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useWizard } from "@/lib/wizard-context";
import { useTenant } from "@/lib/tenant-context";
import WizardProgress from "@/components/WizardProgress";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ALL_CARD_COLORS, ALL_AVATAR_STYLES } from "@/lib/mock-data";
import { ArrowLeft, ArrowRight, Upload, X, Sparkles, Check, RefreshCw, Camera, Image, AlertTriangle, ChevronDown, ChevronUp, Loader2, Coins } from "lucide-react";
import CreditPurchase from "@/components/CreditPurchase";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import type { AvatarStyle, Theme } from "@/lib/types";
import { useStyleLabels } from "@/hooks/use-style-labels";
import { useStyleTaxonomy } from "@/hooks/use-style-taxonomy";
import { supabase } from "@/integrations/supabase/client";

const CardConfigurator = () => {
  const { tenant_slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useTenant();
  const {
    config, setConfig,
    avatarVariants, selectedAvatarId, setSelectedAvatarId,
    attempts, setAttempts, handleGenerate, generating,
    usePhotoOnly, setUsePhotoOnly,
    processingBgIds,
  } = useWizard();
  const initialStep = (location.state as any)?.returnToStep ?? 0;
  const [step, setStep] = useState(initialStep);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [guidelinesOpen, setGuidelinesOpen] = useState(true);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [showPurchase, setShowPurchase] = useState(false);
  const [creditSheetShown, setCreditSheetShown] = useState(false);
  const { applyOverrides } = useStyleLabels();
  const { hasSubStyles, hasHobbies, getSubStylesFor, getHobbiesFor, getHobbiesForSubStyle } = useStyleTaxonomy(tenant?.id, tenant?.taxonomySubStyleOverrides, tenant?.taxonomyHobbyOverrides, tenant?.taxonomyDepthOverrides);

  const allowPhotoOnly = tenant?.allowPhotoOnly ?? false;

  // Sync free attempts from tenant config
  const tenantFreeAttempts = tenant?.freeAttemptsPerUser ?? 5;
  useEffect(() => {
    setAttempts((prev) => ({ ...prev, attemptsFree: tenantFreeAttempts }));
  }, [tenantFreeAttempts, setAttempts]);

  // Fetch user credit balance
  useEffect(() => {
    const fetchCredits = async () => {
      if (!tenant?.id) return;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("user_credits")
        .select("balance")
        .eq("user_id", user.id)
        .eq("tenant_id", tenant.id)
        .maybeSingle();
      setCreditBalance(data?.balance ?? 0);
    };
    fetchCredits();
  }, [tenant?.id, attempts.attemptsUsed]);

  // Use tenant-enabled styles (already filtered by enabledStyles in tenant config)
  // Skip is_active filter since tenant config is the source of truth for which styles are enabled
  const styledAvatarStyles = useMemo(
    () => applyOverrides(tenant?.avatarStyles ?? ALL_AVATAR_STYLES, !tenant?.avatarStyles),
    [tenant?.avatarStyles, applyOverrides]
  );

  // Auto-select the first style (e.g. Cardmoji) when entering the Style step
  useEffect(() => {
    if (!config.style && styledAvatarStyles.length > 0) {
      setConfig((prev) => ({ ...prev, style: styledAvatarStyles[0].id, styleSubChoice: null, hobby: undefined }));
    }
  }, [config.style, styledAvatarStyles, setConfig]);


  const selectedStyleObj = styledAvatarStyles.find((s) => s.id === config.style);

  // Determine what the selected style supports based on taxonomy config
  const styleShowsSubChoices = config.style ? hasSubStyles(config.style) : false;
  const styleShowsHobbies = config.style ? hasHobbies(config.style) : false;

  // DB-driven sub-styles for the selected style (taxonomy is sole source of truth)
  const dbSubStylesForSelected = config.style ? getSubStylesFor(config.style) : [];
  const effectiveSubChoices = dbSubStylesForSelected.map((s) => ({ id: s.id, label: s.display_name, emoji: s.emoji || "🎨" }));
  const showSubChoices = styleShowsSubChoices && effectiveSubChoices.length > 0;

  // Auto-select the first non-portrait sub-style once a style is chosen, so the
  // user lands on a pre-selected sub-style with its features already visible
  // (rather than seeing the union of all sub-styles' features).
  useEffect(() => {
    if (!config.style) return;
    if (config.styleSubChoice) return; // already chosen by the user
    if (!showSubChoices) return;
    const firstNonPortrait = effectiveSubChoices.find((s) => s.id !== "portrait");
    if (firstNonPortrait) {
      setConfig((prev) => ({ ...prev, styleSubChoice: firstNonPortrait.id }));
    }
  }, [config.style, config.styleSubChoice, showSubChoices, effectiveSubChoices, setConfig]);

  // DB-driven hobbies — scoped to selected sub-style if available, else all for the style
  // Taxonomy mappings are the source of truth for feature visibility.
  const dbHobbies = config.styleSubChoice
    ? getHobbiesForSubStyle(config.styleSubChoice)
    : config.style
      ? getHobbiesFor(config.style)
      : [];

  const effectiveHobbies = dbHobbies.length > 0
    ? dbHobbies.map((h) => ({ id: h.id, label: h.display_name, emoji: h.emoji || "🎯" }))
    : [];

  // Steps change depending on whether user picks "photo only" and taxonomy config
  const hasAvailableHobbies = effectiveHobbies.length > 0;
  const getSteps = () => {
    if (usePhotoOnly) {
      return ["Mode", "Photo", "Done"];
    }
    const steps = ["Mode", "Style"];
    steps.push("Selfie", "Generate", "Pick");
    return steps;
  };

  const STEPS = getSteps();

  const canProceed = () => {
    const stepName = STEPS[step];
    if (stepName === "Mode") return true; // always can proceed — a mode is always selected
    if (stepName === "Style") {
      if (!config.style) return false;
      // If Portrait is among sub-choices, user can either pick Portrait (no hobby needed) or pick a hobby
      const hasPortraitSub = effectiveSubChoices.some((s) => s.id === "portrait");
      if (hasPortraitSub) {
        // Valid if Portrait is selected OR a hobby is selected
        return config.styleSubChoice === "portrait" || !!config.hobby;
      }
      if (showSubChoices && !config.styleSubChoice) return false;
      if (styleShowsHobbies && hasAvailableHobbies && !config.hobby) return false;
      return true;
    }
    if (stepName === "Selfie") return true;
    if (stepName === "Photo") return !!config.selfieFile;
    if (stepName === "Generate") return avatarVariants.length > 0;
    if (stepName === "Pick") return !!selectedAvatarId;
    if (stepName === "Done") return !!config.selfieFile;
    return false;
  };

  const handleNext = () => {
    const stepName = STEPS[step];
    if (stepName === "Pick" || stepName === "Done") {
      navigate(`/t/${tenant_slug}/editor`);
    } else {
      setStep(step + 1);
    }
  };

  const handleSelfieChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setConfig((prev) => ({ ...prev, selfieFile: file }));
      setSelfiePreview(URL.createObjectURL(file));
    }
  };

  // handleRefChange/removeRef deleted with the dead reference-image UI.


  const stepName = STEPS[step];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="border-b px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => step > 0 ? setStep(step - 1) : navigate(`/t/${tenant_slug}/consent`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="font-display font-semibold text-sm">{tenant?.brandName || "Cardmoji"}</span>
      </div>

      <WizardProgress steps={STEPS} currentStep={step} />

      <div className="flex-1 px-4 pb-28 animate-fade-in">
        {/* Mode selection: Avatar vs Photo */}
        {stepName === "Mode" && (
          <div className="max-w-lg mx-auto space-y-4">
            <h2 className="text-xl font-bold font-display">How Would You Like to Personalise?</h2>
            <div className="grid grid-cols-1 gap-3">
              <button
                className={cn(
                  "flex items-center gap-4 rounded-xl border bg-card p-5 transition-all shadow-card hover:shadow-elevated text-left",
                  !usePhotoOnly && "ring-2 ring-primary border-primary"
                )}
                onClick={() => setUsePhotoOnly(false)}
              >
                <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-sm">AI-Generated Avatar</p>
                  <p className="text-xs text-muted-foreground">Upload a selfie and we'll create a unique avatar in your chosen style</p>
                </div>
                {!usePhotoOnly && <Check className="h-5 w-5 text-primary ml-auto shrink-0" />}
              </button>

              {allowPhotoOnly && (
                <button
                  className={cn(
                    "flex items-center gap-4 rounded-xl border bg-card p-5 transition-all shadow-card hover:shadow-elevated text-left",
                    usePhotoOnly && "ring-2 ring-primary border-primary"
                  )}
                  onClick={() => setUsePhotoOnly(true)}
                >
                  <div className="h-12 w-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
                    <Camera className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Use My Photo</p>
                    <p className="text-xs text-muted-foreground">Print your own photo directly on the card — no avatar generation</p>
                  </div>
                  {usePhotoOnly && <Check className="h-5 w-5 text-primary ml-auto shrink-0" />}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Style — horizontal scroll row + sub-styles & features below */}
        {stepName === "Style" && (
          <div className="max-w-2xl mx-auto space-y-4">
            {styledAvatarStyles.length > 1 ? (
              <>
                <h2 className="text-xl font-bold font-display">Choose Your Style</h2>

                {/* Horizontally scrollable style row */}
                <div className="overflow-x-auto -mx-4 px-4 pb-2">
                  <div className="flex gap-2" style={{ minWidth: "max-content" }}>
                    {styledAvatarStyles.map((s) => (
                      <button
                        key={s.id}
                        className={cn(
                          "flex flex-col items-center gap-1 rounded-xl border bg-card p-1.5 transition-all shadow-card hover:shadow-elevated overflow-hidden shrink-0",
                          "w-[90px]",
                          config.style === s.id && "ring-2 ring-primary border-primary"
                        )}
                        onClick={() => setConfig((prev) => ({ ...prev, style: s.id, styleSubChoice: null, hobby: undefined }))}
                      >
                        {s.templateImage ? (
                          <img src={s.templateImage} alt={s.label} className="w-full aspect-square rounded-lg object-cover" />
                        ) : (
                          <div className="w-full aspect-square rounded-lg bg-muted flex items-center justify-center text-2xl">
                            {s.emoji}
                          </div>
                        )}
                        <span className="font-semibold text-[10px] text-center leading-tight line-clamp-2">{s.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <h2 className="text-xl font-bold font-display">
                Customise your {selectedStyleObj?.label || "style"}
              </h2>
            )}

            {/* Sub-choices and features — flattened when Portrait is among sub-choices */}
            {config.style && (() => {
              const hasPortrait = effectiveSubChoices.some((s) => s.id === "portrait");
              const nonPortraitSubs = effectiveSubChoices.filter((s) => s.id !== "portrait");
              const showFlatGrid = hasPortrait && showSubChoices;

              return (
                <>
                  {/* Standard sub-choices (only non-portrait ones, if portrait exists) */}
                  {showSubChoices && nonPortraitSubs.length > 0 && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-muted-foreground">
                        Choose your {selectedStyleObj?.label?.toLowerCase() || "style"} option
                      </h3>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {nonPortraitSubs.map((sub) => (
                          <button
                            key={sub.id}
                            className={cn(
                              "flex flex-col items-center gap-1 rounded-lg border bg-card p-3 transition-all hover:shadow-card",
                              config.styleSubChoice === sub.id && "ring-2 ring-primary border-primary"
                            )}
                            onClick={() => setConfig((prev) => ({ ...prev, styleSubChoice: sub.id, hobby: undefined }))}
                          >
                            <span className="text-xl">{sub.emoji}</span>
                            <span className="text-xs font-medium">{sub.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Features grid — include Portrait as an option alongside features */}
                  {showFlatGrid && (hasAvailableHobbies || hasPortrait) && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-muted-foreground">Pick a theme</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {/* Portrait option — selecting it clears sub-choice and hobby */}
                        <button
                          className={cn(
                            "flex items-center gap-2 rounded-xl border bg-card p-3 transition-all shadow-card hover:shadow-elevated",
                            config.styleSubChoice === "portrait" && !config.hobby && "ring-2 ring-primary border-primary"
                          )}
                          onClick={() => setConfig((prev) => ({ ...prev, styleSubChoice: "portrait", hobby: undefined }))}
                        >
                          <span className="text-lg">🖼️</span>
                          <span className="font-medium text-sm">Portrait</span>
                        </button>
                        {effectiveHobbies.map((h) => (
                          <button
                            key={h.id}
                            className={cn(
                              "flex items-center gap-2 rounded-xl border bg-card p-3 transition-all shadow-card hover:shadow-elevated",
                              config.hobby === h.id && "ring-2 ring-primary border-primary"
                            )}
                            onClick={() => setConfig((prev) => ({ ...prev, hobby: h.id as Theme, styleSubChoice: prev.styleSubChoice === "portrait" ? null : prev.styleSubChoice }))}
                          >
                            <span className="text-lg">{h.emoji}</span>
                            <span className="font-medium text-sm">{h.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Standard features (no portrait in sub-choices) */}
                  {!showFlatGrid && styleShowsHobbies && hasAvailableHobbies && (
                    <div className="space-y-2">
                      <h3 className="text-sm font-semibold text-muted-foreground">Pick a theme</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {effectiveHobbies.map((h) => (
                          <button
                            key={h.id}
                            className={cn(
                              "flex items-center gap-2 rounded-xl border bg-card p-3 transition-all shadow-card hover:shadow-elevated",
                              config.hobby === h.id && "ring-2 ring-primary border-primary"
                            )}
                            onClick={() => setConfig((prev) => ({ ...prev, hobby: h.id as Theme }))}
                          >
                            <span className="text-lg">{h.emoji}</span>
                            <span className="font-medium text-sm">{h.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Standard sub-choices (no portrait) */}
                  {!hasPortrait && showSubChoices && effectiveSubChoices.length > 0 && nonPortraitSubs.length === 0 && null}
                </>
              );
            })()}
          </div>
        )}

        {/* Selfie Upload (avatar flow) */}
        {stepName === "Selfie" && (
          <div className="max-w-lg mx-auto space-y-4">
            <h2 className="text-xl font-bold font-display">Upload Your Selfie</h2>
            <input ref={fileInputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={handleSelfieChange} />
            
            {selfiePreview ? (
              <div className="relative">
                <img src={selfiePreview} alt="Selfie preview" className="w-full max-h-64 object-cover rounded-xl shadow-card" />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-7 w-7"
                  onClick={() => {
                    setConfig((prev) => ({ ...prev, selfieFile: null }));
                    setSelfiePreview(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <button
                className="w-full flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-muted-foreground/30 bg-muted/30 p-10 hover:bg-muted/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Tap to take a selfie or upload a photo</span>
              </button>
            )}

            {/* Content Guidelines */}
            <button
              onClick={() => setGuidelinesOpen(!guidelinesOpen)}
              className="w-full flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-4 py-3 text-left transition-colors hover:bg-amber-100 dark:hover:bg-amber-950/50"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4" />
                Image Content Guidelines
              </span>
              {guidelinesOpen ? <ChevronUp className="h-4 w-4 text-amber-600" /> : <ChevronDown className="h-4 w-4 text-amber-600" />}
            </button>
            {guidelinesOpen && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20 px-4 py-3 space-y-2">
                <p className="text-xs text-muted-foreground">You must own the image or have permission to use it. Images with copyright, trademarks, or watermarks are not allowed.</p>
                <p className="text-xs font-medium text-amber-800 dark:text-amber-300">Prohibited content:</p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  <li>Famous figures, celebrities, cartoon characters, or branded products</li>
                  <li>Political, religious, sexual, violent, or offensive subject matter</li>
                  <li>Money, currency, weapons, alcohol, drugs, or gambling imagery</li>
                  <li>Contact information (phone numbers, addresses, emails, social media)</li>
                  <li>Slogans, taglines, or marketing/promotional content</li>
                  <li>Edited or altered national flags</li>
                </ul>
                <p className="text-xs text-muted-foreground">Images must not reflect poorly on, or engender hostility toward, the card network or issuing bank.</p>
              </div>
            )}

            {/* Reference-image upload removed: the files were collected but never
                sent to generate-avatar, so the control was purely decorative. */}

          </div>
        )}

        {/* Photo Upload (photo-only flow) */}
        {stepName === "Photo" && (
          <div className="max-w-lg mx-auto space-y-4">
            <h2 className="text-xl font-bold font-display">Upload Your Photo</h2>
            <p className="text-sm text-muted-foreground">This photo will be printed directly on your card.</p>
            <input ref={fileInputRef} type="file" accept="image/*" capture="user" className="hidden" onChange={handleSelfieChange} />
            
            {selfiePreview ? (
              <div className="relative">
                <img src={selfiePreview} alt="Photo preview" className="w-full max-h-80 object-cover rounded-xl shadow-card" />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 h-7 w-7"
                  onClick={() => {
                    setConfig((prev) => ({ ...prev, selfieFile: null }));
                    setSelfiePreview(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <button
                className="w-full flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-muted-foreground/30 bg-muted/30 p-10 hover:bg-muted/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Image className="h-8 w-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Tap to upload or take a photo</span>
              </button>
            )}

            {/* Content Guidelines */}
            <button
              onClick={() => setGuidelinesOpen(!guidelinesOpen)}
              className="w-full flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-4 py-3 text-left transition-colors hover:bg-amber-100 dark:hover:bg-amber-950/50"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4" />
                Image Content Guidelines
              </span>
              {guidelinesOpen ? <ChevronUp className="h-4 w-4 text-amber-600" /> : <ChevronDown className="h-4 w-4 text-amber-600" />}
            </button>
            {guidelinesOpen && (
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20 px-4 py-3 space-y-2">
                <p className="text-xs text-muted-foreground">You must own the image or have permission to use it. Images with copyright, trademarks, or watermarks are not allowed.</p>
                <p className="text-xs font-medium text-amber-800 dark:text-amber-300">Prohibited content:</p>
                <ul className="text-xs text-muted-foreground space-y-1 list-disc pl-4">
                  <li>Famous figures, celebrities, cartoon characters, or branded products</li>
                  <li>Political, religious, sexual, violent, or offensive subject matter</li>
                  <li>Money, currency, weapons, alcohol, drugs, or gambling imagery</li>
                  <li>Contact information (phone numbers, addresses, emails, social media)</li>
                  <li>Slogans, taglines, or marketing/promotional content</li>
                  <li>Edited or altered national flags</li>
                </ul>
                <p className="text-xs text-muted-foreground">Images must not reflect poorly on, or engender hostility toward, the card network or issuing bank.</p>
              </div>
            )}
          </div>
        )}

        {/* Generate (avatar flow) */}
        {stepName === "Generate" && (() => {
          const freeRemaining = Math.max(0, attempts.attemptsFree - attempts.attemptsUsed);
          const hasFreeAttempts = freeRemaining > 0;
          const hasCredits = (creditBalance ?? 0) > 0;
          const canGenerate = hasFreeAttempts || hasCredits;

          return (
          <div className="max-w-lg mx-auto space-y-6">
            <h2 className="text-xl font-bold font-display">Generate Your Avatar</h2>
            
            {/* Smart counter: show free attempts if available, otherwise show credits */}
            <div className="flex items-center justify-between rounded-xl bg-muted/50 px-4 py-3">
              {hasFreeAttempts ? (
                <>
                  <span className="text-sm">Free attempts remaining</span>
                  <span className="font-semibold text-sm">{freeRemaining}</span>
                </>
              ) : hasCredits ? (
                <>
                  <span className="text-sm flex items-center gap-1.5"><Coins className="h-4 w-4 text-primary" /> Credits available</span>
                  <span className="font-semibold text-sm">{creditBalance}</span>
                </>
              ) : (
                <>
                  <span className="text-sm text-destructive">No attempts or credits remaining</span>
                  <span className="font-semibold text-sm text-destructive">0</span>
                </>
              )}
            </div>

            {avatarVariants.length > 0 ? (
              <div className="relative">
                <div className="grid grid-cols-2 gap-4">
                {avatarVariants.map((v) => (
                  <div
                    key={v.id}
                    onClick={() => !processingBgIds.has(v.id) && setSelectedAvatarId(v.id)}
                    className={cn(
                      "cursor-pointer rounded-xl border-2 transition-all overflow-hidden",
                      selectedAvatarId === v.id ? "border-primary ring-2 ring-primary/30 shadow-lg" : "border-border hover:border-primary/50"
                    )}
                  >
                    {processingBgIds.has(v.id) ? (
                      <div className="aspect-square w-full bg-muted flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <img src={v.imageUrl} alt={`Variant ${v.variantNumber}`} className="aspect-square w-full object-cover bg-muted" />
                    )}
                    <div className="p-2 text-center text-xs text-muted-foreground">
                      {processingBgIds.has(v.id) ? "Processing…" : `Variant ${v.variantNumber}`}
                    </div>
                  </div>
                ))}
                </div>
                {generating && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-background/80 backdrop-blur-sm animate-fade-in">
                    <GeneratingCountdown />
                  </div>
                )}
              </div>
            ) : generating ? (
              <GeneratingCountdown />
            ) : !canGenerate ? (
              /* Out of credits — prompt to open bottom sheet */
              <div className="flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-destructive/30 bg-destructive/5 p-8 text-center">
                <Coins className="h-10 w-10 text-muted-foreground" />
                <p className="text-sm font-medium">You've used all your free attempts</p>
                <p className="text-xs text-muted-foreground">Purchase credits to keep generating avatars</p>
                <Button className="mt-2 gradient-primary text-primary-foreground font-semibold gap-2" onClick={() => setShowPurchase(true)}>
                  <Coins className="h-4 w-4" /> Buy Credits
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed p-10 text-center">
                <Sparkles className="h-10 w-10 text-primary" />
                <p className="text-muted-foreground text-sm">Click generate to create 2 avatar variants from your selfie</p>
              </div>
            )}

            {canGenerate && (
              <Button
                className="w-full gradient-primary text-primary-foreground font-semibold gap-2"
                onClick={() => handleGenerate(tenant?.id || "")}
                disabled={generating}
              >
                {generating ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Generating...</>
                ) : avatarVariants.length > 0 ? (
                  <><RefreshCw className="h-4 w-4" /> Regenerate {!hasFreeAttempts && "(1 credit)"}</>
                ) : (
                  <><Sparkles className="h-4 w-4" /> Generate Avatars {!hasFreeAttempts && "(1 credit)"}</>
                )}
              </Button>
            )}

            {/* Show "buy more" link when free attempts exhausted but user still has some credits */}
            {!hasFreeAttempts && hasCredits && (
              <button className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 pt-2" onClick={() => setShowPurchase(true)}>
                Need more credits?
              </button>
            )}
          </div>
          );
        })()}

        {/* Pick favourite (avatar flow) */}
        {stepName === "Pick" && (
          <div className="max-w-lg mx-auto space-y-4">
            <h2 className="text-xl font-bold font-display">Pick Your Favourite</h2>
            <p className="text-sm text-muted-foreground">
              {avatarVariants.length > 2
                ? `${avatarVariants.length} variants from ${Math.ceil(avatarVariants.length / 2)} generation${Math.ceil(avatarVariants.length / 2) > 1 ? "s" : ""}`
                : "Choose the avatar you like best"}
            </p>
            <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
              {avatarVariants.map((v, idx) => (
                <button
                  key={v.id}
                  className={cn(
                    "rounded-xl border bg-card shadow-card overflow-hidden transition-all",
                    selectedAvatarId === v.id && "ring-2 ring-primary ring-offset-2"
                  )}
                  onClick={() => setSelectedAvatarId(v.id)}
                >
                  {processingBgIds.has(v.id) || !v.imageUrl ? (
                      <div className="aspect-square w-full bg-muted animate-pulse flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <img src={v.imageUrl} alt={`Variant ${idx + 1}`} className="aspect-square w-full object-cover bg-muted" />
                    )}
                  <div className="p-3 flex items-center justify-center gap-2">
                    {selectedAvatarId === v.id ? (
                      <><Check className="h-4 w-4 text-primary" /><span className="text-sm font-semibold text-primary">Selected</span></>
                    ) : (
                      <span className="text-sm text-muted-foreground">Variant {idx + 1}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Done confirmation (photo-only flow) */}
        {stepName === "Done" && (
          <div className="max-w-lg mx-auto space-y-4 text-center">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-accent/10 flex items-center justify-center">
                <Check className="h-8 w-8 text-accent" />
              </div>
            </div>
            <h2 className="text-xl font-bold font-display">Photo Ready!</h2>
            <p className="text-sm text-muted-foreground">Your photo will be used directly on your card. Continue to position and adjust it.</p>
          </div>
        )}
      </div>

      {/* Credit purchase bottom sheet */}
      <Drawer open={showPurchase} onOpenChange={setShowPurchase}>
        <DrawerContent>
          <div className="mx-auto w-full max-w-lg px-4 pb-8">
            <DrawerHeader className="px-0">
              <DrawerTitle className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-primary" /> Buy More Credits
              </DrawerTitle>
              <DrawerDescription>
                Choose a credit pack to continue generating avatars.
              </DrawerDescription>
            </DrawerHeader>
            <CreditPurchase
              tenantId={tenant?.id || ""}
              onPurchaseComplete={() => {
                setCreditBalance(null);
                setShowPurchase(false);
              }}
            />
          </div>
        </DrawerContent>
      </Drawer>

      {/* Fixed bottom */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[960px] bg-background/80 backdrop-blur border-t p-4">
        <div className="max-w-lg mx-auto flex gap-3">
          {step > 0 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} className="flex-1">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          )}
          {stepName === "Generate" ? (
            avatarVariants.length > 0 && (
              <Button
                className="flex-1 gradient-primary text-primary-foreground font-semibold gap-2"
                onClick={handleNext}
              >
                Pick Favourite <ArrowRight className="h-4 w-4" />
              </Button>
            )
          ) : (
            <Button
              className={cn("flex-1 gradient-primary text-primary-foreground font-semibold gap-2", step === 0 && "w-full")}
              disabled={!canProceed()}
              onClick={handleNext}
            >
              {stepName === "Pick" || stepName === "Done" ? "Confirm & Edit Card" : "Continue"}
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CardConfigurator;
